import { Alert, Box, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React from 'react';
import ItemBuilder from '../../features/item-builder/ItemBuilder';
import { useAppSelector } from '../../app/hooks';
import { useTaskLoadingMetricsContext } from '../../hooks/taskLoadingMetrics/TaskLoadingMetricsContext';
import { selectIsHitl7Enabled } from '../../redux/features/config/config.selector';
import { SetCartItemRef } from '../../types';
import Colors from '../../utils/color';
import {
  HITL3_PADDING,
  HITL7_BOTTOM_SECTION_HEIGHT,
  HITL7_OMNIBAR_INSET_Y,
} from '../../utils/hitl3Spacing';
import TaskLoadingMetricsDisplay from '../TaskLoadingMetricsDisplay';
import BannerCard from './BannerCard';
import { OmniBarCompact } from './OmniBarCompact';
import useConnectionAlerts from './hooks/useConnectionAlerts.hooks';

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  middlePane: {
    height: '100%',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    // Anchor for the absolutely-positioned WebSocket connection alerts.
    position: 'relative',
  },
  alertClass: {
    position: 'absolute',
    left: spacing(2),
    right: spacing(2),
    zIndex: 200,
    alignItems: 'center',
  },
  // Single center card: Current Step section pinned at the top, option groups
  // scrolling below it. Flush like the side panes — no border/radius; the pane
  // dividers supply the only separation.
  centerCard: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: Colors.white,
    overflow: 'hidden',
  },
  optionScroll: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: 0,
    // ItemBuilder keeps its original spacing(7) top padding (it makes room for
    // the Banner overlay in the OLD layout). Here BannerCard is a separate flex
    // child above, so drop that top gap to keep the option groups flush.
    '& #itemBuilder': {
      paddingTop: 0,
    },
  },
}));

interface CenterPaneProps {
  setCartItemRef: SetCartItemRef;
}

/**
 * MIDDLE pane of the HITL console: task-loading metrics readout, the WebSocket
 * connection alerts, and the single center card (current-step banner pinned at
 * the top + the scrolling option groups from ItemBuilder).
 *
 * HITL-1664 (hitl_7_0): the typing box is pinned below that card, at the foot of
 * the pane. It sits OUTSIDE `centerCard` so the option groups keep the flexible
 * height and the input never scrolls away with them.
 */
const CenterPane = ({ setCartItemRef }: CenterPaneProps) => {
  const classes = useStyles();
  const isHitl7 = useAppSelector(selectIsHitl7Enabled);
  const { lastSentMetrics } = useTaskLoadingMetricsContext();
  const {
    eventWSConnectionMessage,
    audioWSConnectionMessage,
    eventWSConnectionSuccess,
    audioWSConnectionSuccess,
    showEventAlert,
    handleCloseEventAlert,
    showAudioAlert,
    handleCloseAudioAlert,
  } = useConnectionAlerts();

  return (
    <div className={classes.middlePane}>
      <TaskLoadingMetricsDisplay metrics={lastSentMetrics} />
      {showEventAlert && eventWSConnectionMessage && (
        <Alert
          variant="filled"
          severity={eventWSConnectionSuccess ? 'success' : 'error'}
          className={classes.alertClass}
          onClose={eventWSConnectionSuccess ? handleCloseEventAlert : undefined}
        >
          Event WebSocket: {eventWSConnectionMessage}
        </Alert>
      )}
      {showAudioAlert && audioWSConnectionMessage && (
        <Alert
          variant="filled"
          severity={audioWSConnectionSuccess ? 'success' : 'error'}
          className={classes.alertClass}
          onClose={audioWSConnectionSuccess ? handleCloseAudioAlert : undefined}
          sx={{
            top:
              showEventAlert && eventWSConnectionMessage ? '72px' : undefined,
          }}
        >
          Audio WebSocket: {audioWSConnectionMessage}
        </Alert>
      )}
      <div className={classes.centerCard}>
        <BannerCard />
        <div className={classes.optionScroll}>
          <ItemBuilder setCartItemRef={setCartItemRef} />
        </div>
      </div>
      {/* HITL-1664: the typing box, at the foot of the pane and at full pane
          width. `flexShrink: 0` keeps it pinned while the option groups above
          take the slack, so it holds its one row at every pane height. Styling
          is `sx` rather than a new `useStyles` rule on purpose: adding a rule to
          a live makeStyles block advances the GLOBAL JSS class-name counter and
          renumbers names in unrelated suites' snapshots.

          Mounting is gated on the same selector BottomActionBar reads, so the
          box exists exactly once — the input registers itself with
          `setOmniBarInput` on mount, and two live inputs would leave the focus
          handback pointing at whichever mounted last. Nothing about focus is
          touched here: the handback resolves a NODE registered by a stable
          callback ref, not a DOM path, so it is indifferent to where the input
          sits. Grabbing focus on render instead has twice shipped a regression
          that made other surfaces untypable (MR !1593). */}
      {isHitl7 && (
        <Box
          sx={{
            flexShrink: 0,
            backgroundColor: Colors.white,
            borderTop: `1px solid ${Colors.alto}`,
            // 6px top/bottom instead of the usual 8px so the section totals
            // exactly HITL7_BOTTOM_SECTION_HEIGHT: 1px rule + 6 + 34px input +
            // 6 = 47. The horizontal inset stays at 8px so the input still
            // lines up with the pane content above it.
            padding: `${HITL7_OMNIBAR_INSET_Y} ${HITL3_PADDING}`,
            // HITL-1665: the exact shared height all three bottom sections
            // take, so their top rules land on one line. `border-box` so the
            // 1px rule is INSIDE the 47 rather than on top of it.
            //
            // An exact height, not a minimum: this box's natural content is
            // 51px, so a 47px floor would have done nothing at all — which is
            // exactly what a `minHeight` attempt here did.
            boxSizing: 'border-box',
            height: HITL7_BOTTOM_SECTION_HEIGHT,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <OmniBarCompact />
        </Box>
      )}
    </div>
  );
};

export default CenterPane;
